import AppContext from 'App/AppContext';
import { IPDefaultButton, IPSpinner, IPSpinnerSize, IPStack, IPText } from 'baseComponents';
import GenericModal from 'components/Dialogs/GenericModal';
import { globalStackTokensGapSmall } from 'globalStyles';
import KPIData from 'models/kpi/kpiData';
import TaskType from 'models/tasks/taskType';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetFormVersion, apiGetFormVersionHistory, apiGetTaskTypeForm } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { sortOnNumber } from 'utils/sorting';

interface IFormStructureConflictModalProps {
  taskTypeFamilyId: number;
  localFormElements: TaskTypeFormElement[];
  isOpen: boolean;
  onDismiss: () => void;
  onReload: (taskType: TaskType, formElements: TaskTypeFormElement[]) => void;
  onReapply: (taskType: TaskType, formElements: TaskTypeFormElement[]) => Promise<boolean>;
}

interface IFormStructureConflictRow {
  key: string;
  local?: TaskTypeFormElement;
  server?: TaskTypeFormElement;
  status: 'localOnly' | 'serverOnly' | 'removedOnServer' | 'changed';
}

const getElementKey = (element: TaskTypeFormElement): string => element.fieldKey ?? `local:${element.key}`;

const hasSameStructure = (left: TaskTypeFormElement, right: TaskTypeFormElement): boolean =>
  left.fieldType === right.fieldType &&
  left.fieldMode === right.fieldMode &&
  left.kpiId === right.kpiId &&
  left.kpiDefinitionRevisionId === right.kpiDefinitionRevisionId &&
  left.sortOrder === right.sortOrder &&
  left.listId === right.listId &&
  left.folderId === right.folderId &&
  left.name === right.name &&
  left.description === right.description &&
  left.instruction === right.instruction &&
  left.expectedResult === right.expectedResult;

const buildConflictRows = (
  localElements: TaskTypeFormElement[],
  serverElements: TaskTypeFormElement[],
): IFormStructureConflictRow[] => {
  const localByKey = new Map(localElements.map((element) => [getElementKey(element), element]));
  const serverByKey = new Map(serverElements.map((element) => [getElementKey(element), element]));

  return [...new Set([...localByKey.keys(), ...serverByKey.keys()])]
    .map((key): IFormStructureConflictRow | undefined => {
      const local = localByKey.get(key);
      const server = serverByKey.get(key);
      if (!local) {
        return { key, server, status: 'serverOnly' };
      }

      if (!server) {
        // A draft element WITH a field key once existed on the server, so its absence there means
        // the concurrent update deleted it; one without a key is the user's own unsaved addition.
        return { key, local, status: local.fieldKey ? 'removedOnServer' : 'localOnly' };
      }

      return hasSameStructure(local, server) ? undefined : { key, local, server, status: 'changed' };
    })
    .filter((row): row is IFormStructureConflictRow => row !== undefined);
};

const FormStructureConflictModal = (props: IFormStructureConflictModalProps) => {
  const { t } = useTranslation(['forms']);
  const appContext = useContext(AppContext);
  const [serverTaskType, setServerTaskType] = useState<TaskType | undefined>(undefined);
  const [serverElements, setServerElements] = useState<TaskTypeFormElement[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isApplying, setIsApplying] = useState<boolean>(false);

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    const loadCurrent = async () => {
      try {
        setIsLoading(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const history = await apiGetFormVersionHistory(props.taskTypeFamilyId, accessToken);
        const current = history.find((version) => version.isCurrent);
        if (!current) {
          throw new Error('The form family has no current version.');
        }

        const [taskType, elements] = await Promise.all([
          apiGetFormVersion(props.taskTypeFamilyId, current.taskTypeId, accessToken),
          apiGetTaskTypeForm(current.taskTypeId, accessToken),
        ]);

        elements.sort((a, b) => sortOnNumber(a.sortOrder, b.sortOrder));
        //every field renders from a data row, and a form being designed has no answers to read
        elements.forEach((element) => {
          if (element.kpi) {
            const newData = new KPIData();
            element.kpi.addData([newData]);
          }
        });

        setServerTaskType(taskType);
        setServerElements(elements);
      } catch (error) {
        appContext.setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    void loadCurrent();
  }, [appContext, props.isOpen, props.taskTypeFamilyId]);

  const conflictRows = buildConflictRows(props.localFormElements, serverElements);

  const getStatusText = (status: IFormStructureConflictRow['status']): string => {
    switch (status) {
      case 'localOnly':
        return t('forms:Versioning.Conflict.LocalOnly');
      case 'serverOnly':
        return t('forms:Versioning.Conflict.ServerOnly');
      case 'removedOnServer':
        return t('forms:Versioning.Conflict.RemovedOnServer');
      case 'changed':
        return t('forms:Versioning.Conflict.Changed');
    }
  };

  const getElementName = (element: TaskTypeFormElement | undefined): string =>
    element?.name || element?.kpi?.name || t('forms:Versioning.UnnamedField');

  const reload = () => {
    if (!serverTaskType) {
      return;
    }

    props.onReload(serverTaskType, serverElements);
    props.onDismiss();
  };

  const reapply = async () => {
    if (!serverTaskType) {
      return;
    }

    // A field the concurrent update DELETED matches nothing server-side any more, and a draft that
    // still carries its identity is rejected on every retry - reapply could then never succeed.
    // Strip that identity so the field is re-created as a new one; the server assigns a fresh key
    // and re-pins the KPI's current definition revision itself.
    const serverFieldKeys = new Set(serverElements.map((element) => element.fieldKey));
    const serverFieldIds = new Set(serverElements.map((element) => element.fieldId));
    const draftElements = props.localFormElements.map((element) => {
      const isNewElement = !element.fieldKey && !(element.fieldId > 0);
      const existsOnServer = element.fieldKey
        ? serverFieldKeys.has(element.fieldKey)
        : serverFieldIds.has(element.fieldId);
      if (isNewElement || existsOnServer) {
        return element;
      }

      const resurrected = element.clone();
      resurrected.fieldId = 0;
      resurrected.fieldKey = undefined;
      resurrected.kpiDefinitionRevisionId = undefined;
      resurrected.kpiDefinitionRevisionRowVersion = undefined;

      return resurrected;
    });

    setIsApplying(true);
    const accepted = await props.onReapply(serverTaskType, draftElements);
    setIsApplying(false);
    if (accepted) {
      props.onDismiss();
    }
  };

  return (
    <GenericModal
      title={t('forms:Versioning.Conflict.Title')}
      subTitle={t('forms:Versioning.Conflict.Text')}
      isOpen={props.isOpen}
      onClose={props.onDismiss}
      onSave={reapply}
      keepOpenAfterSave
      isActionPending={isApplying}
      saveDisabled={isLoading || !serverTaskType}
      saveButtonText={t('forms:Versioning.Conflict.Reapply')}
      closeButtonText={t('forms:Versioning.Conflict.KeepEditing')}
      middleButtons={
        <IPDefaultButton
          iconProps={{ iconName: 'Refresh' }}
          text={t('forms:Versioning.Conflict.Reload')}
          disabled={isLoading || !serverTaskType}
          onClick={reload}
        />
      }
      minWidth={360}
      maxWidth={760}
    >
      {isLoading ? (
        <IPStack horizontalAlign="center" styles={{ root: { paddingTop: 24 } }}>
          <IPSpinner size={IPSpinnerSize.medium} />
        </IPStack>
      ) : (
        <IPStack tokens={globalStackTokensGapSmall}>
          <IPStack horizontal={!appContext.isMobileView}>
            <IPStack.Item grow>
              <IPText variant="mediumPlus">{t('forms:Versioning.Conflict.MyDraft')}</IPText>
            </IPStack.Item>
            <IPStack.Item grow>
              <IPText variant="mediumPlus">
                {t('forms:Versioning.Conflict.ServerVersion', { version: serverTaskType?.version ?? 0 })}
              </IPText>
            </IPStack.Item>
          </IPStack>
          {conflictRows.length === 0 ? (
            <IPText>{t('forms:Versioning.Conflict.MetadataOnly')}</IPText>
          ) : (
            conflictRows.map((row) => (
              <IPStack key={row.key} horizontal={!appContext.isMobileView} tokens={globalStackTokensGapSmall}>
                <IPStack.Item grow>
                  <IPText>{row.local ? getElementName(row.local) : '-'}</IPText>
                </IPStack.Item>
                <IPStack.Item grow>
                  <IPText>{row.server ? getElementName(row.server) : '-'}</IPText>
                </IPStack.Item>
                <IPStack.Item styles={{ root: { flexBasis: 120 } }}>
                  <IPText variant="small">{getStatusText(row.status)}</IPText>
                </IPStack.Item>
              </IPStack>
            ))
          )}
        </IPStack>
      )}
    </GenericModal>
  );
};

export default FormStructureConflictModal;
